import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import SelectIconDropdown from '../../FormInputs/SelectIconDropdown/SelectIconDropdown';
import {
  DateSelectorV2, Number, TextArea, TextInput,
} from '../../FormInputs';
import Button from '../../Button/Button';
import PlaceholderImageWithButton from './PlaceHolderImageWithButton';
import CustomSelect from '../../FormInputs/CustomSelect/CustomSelect';
import HeaderIconTooltip from '../../HeaderIconTooltip/HeaderIconTooltip';
import ToggleButton from '../../ToggleButton/ToggleButton';
import SiteImage from '../SiteImage/SiteImage';
import SitePhotoModal from '../SiteImage/SitePhotoEditModal/SitePhotoEditModal';
import { isUSAZipCode } from '../../../utils/isUSAZipCode';
import { SITE_STATES, SITE_TYPES } from '../../../constants/sites';
import {
  h2aOptions,
  statusOptions,
  columnsClasses,
  inputClasses,
  inputGroupClasses,
  inputInGroupClass,
  labelBesideInputClasses,
  labelBesideLabelClasses,
  labelTextSize,
  sectionsClasses,
  selectFontSize,
  placeholders,
  fontSize,
  asteriskWidth,
  labelFontSize,
  errorMessageClass,
  handleH2AValue,
  spaceY,
} from '../../../utils/siteForm';
import { Plus } from '../../Icons/Icons';
import SiteTags from '../SiteDetailsPage/SiteTags';
import TagModal from '../../Tags/TagsTabbedModal';
import {
  useDialogs, useToasts, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges,
} from '../../../hooks';
import { redirectToSite, redirectToSites } from '../../../utils/navigation';
import { formatTimestampDate, formatNumericDate } from '../../../utils/dates';
import { removeTag } from '../../../utils/api/site';
import CustomFieldsForm from '../../FormInputs/CustomFieldsForm';
import { useUrlModals } from '../../../utils/useUrlModals';

const SiteFormWrapper = ({
  onSave,
  organization,
  siteData,
  setSiteData,
  orgContacts,
  assignedTags,
  setAssignedTags,
  errorMessage,
  primaryContactName,
  setPrimaryContactName,
  organizationTags,
  currentSiteImages,
  setCurrentSiteImages,
  setErrorMessage,
  isSendingForm,
  isUnsavedChanges,
  setCustomFieldsChanged,
  validationMessage,
  setIsUnsavedChanges,
  hasUnsavedChanges,
  shouldScrollToError,
}) => {
  const [unassignedTags, setUnassignedTags] = useState(organizationTags);
  const { openDialog, closeDialog } = useDialogs();
  const { showToast } = useToasts();
  const [siteImages, setSiteImages] = useState([]);

  // Configure modals using useUrlModals
  const modalConfig = {
    tagModal: { isIdParam: false },
    editImages: { isIdParam: false },
  };

  const { modals, openModal, closeModal } = useUrlModals(modalConfig);

  // This should work after implementing the API call to retrieve site data in edit form.
  useEffect(() => {
    setSiteImages(siteData.site_images || []);
  }, [siteData, setSiteImages]);

  useUnsavedChangesWarning(isUnsavedChanges);

  const onDeleteTag = (tag, tagName) => {
    if (siteData.id) {
      // if site is already created, we need to call the API to remove the tag
      removeTag(tag.id, siteData.id, organization.id)
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            setAssignedTags((prevAssignedTags) => prevAssignedTags.filter(({ id }) => id !== tag.id));
            setUnassignedTags([...unassignedTags, { id: tag.id, name: tagName }]);
          }
        })
        .catch((error) => {
          showToast({ message: error.message, type: 'error' });
        });
    } else {
      // if site is not created yet, we can remove the tag from the state
      setAssignedTags((prevAssignedTags) => prevAssignedTags.filter(({ id }) => id !== tag.id));
      setUnassignedTags((prevUnassignedTags) => [...prevUnassignedTags, { id: tag.id, name: tagName }]);
      setSiteData((prevSiteData) => ({
        ...prevSiteData,
        tags: prevSiteData.tags?.filter(({ id }) => id !== tag.id) || [],
      }));
    }
  };

  const previousPage = () => {
    if (siteData.id) {
      redirectToSite(siteData.id);
    } else {
      redirectToSites();
    }
  };

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges,
    redirectCallback: previousPage,
  });

  const onClickCancel = () => {
    handleCloseDialog();
  };

  const handleOpenDeleteTagModal = (tagId, tagName) => {
    openDialog({
      message: `Remove the tag '${tagName}' from this site?`,
      show: true,
      actions: [
        {
          variant: 'secondary',
          label: 'Cancel',
          onClick: closeDialog,
        },
        {
          label: 'Remove',
          onClick: () => {
            onDeleteTag(tagId, tagName);
            closeDialog();
          },
        },
      ],
    });
  };

  const handleOpenModal = () => {
    openModal('editImages');
  };

  const handleCloseModal = () => {
    closeModal('editImages');
  };

  const onCreateTag = (tagData) => {
    const tagsArray = Array.isArray(tagData) ? tagData : [tagData];
    setAssignedTags((prevAssignedTags) => [...prevAssignedTags, ...tagsArray]);
    setUnassignedTags((prevUnassignedTags) => prevUnassignedTags.filter(
      (tag) => !tagsArray.some((newTag) => newTag.id === tag.id),
    ));
    closeModal('tagModal');
  };

  const toggleCoordinatesButton = () => {
    setSiteData((prev) => ({ ...prev, coords_set_manually: !prev.coords_set_manually }));
  };

  // modifying existing data to match the format of props for the redesigned Select component

  const siteTypeOptions = useMemo(() => SITE_TYPES.map((type) => ({
    label: type,
  })), []);

  const stateOptions = useMemo(() => SITE_STATES.map(state => ({
    label: state.name,
    value: state.value,
  })), []);

  // including drop primary contact option to the contacts dropdown
  const contactOptions = useMemo(() => orgContacts.map((contact) => ({
    label: contact.name,
    value: contact.id,
  })), [orgContacts]);

  // we need to display the same text as placeholder id user choose the 'Select' option
  const customSingleValue = (data) => {
    const newLabel = data.label.replace('Select', 'select h2a status');
    return (
      <div className="flex items-center">
        <span>{newLabel}</span>
      </div>
    );
  };

  const handleSiteDataChange = (field, value) => {
    setSiteData((prev) => {
      // Only update state if the new value is different
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
    // clear the error message for the field being changed
    setErrorMessage((prev) => ({
      ...prev,
      [field]: '',
    }));
  };

  // transforming siteData state to display state label in the select component. In the backend data we can have state value or state label
  const handleDisplayState = (value) => {
    const state = stateOptions.find(({ label: stateLabel }) => stateLabel === value) || stateOptions.find(({ value: stateValue }) => stateValue === value);
    return state?.label || '';
  };

  // transform status value to display status label in the select component. Status on backend saves as a number, but we need to display it as a string
  const handleDisplayStatus = (value) => {
    const status = statusOptions.find(({ label: statusLabel }) => statusLabel === value) || statusOptions.find(({ value: statusValue }) => statusValue === value);
    return status?.label || '';
  };

  const handleCloseTagModal = () => {
    closeModal('tagModal');
  };

  const handleOpenTagModal = () => {
    openModal('tagModal');
  };

  return (
    <>
      {/* First line with icons, site name, and buttons. It changes position on displays smaller than 640px, displaying all content in one column with the buttons first. */}
      <div className={`${columnsClasses} 'h-fit'`}>
        {/* icon and site name */}
        <div className="flex gap-4 order-last sm:order-first sm:w-7/12">
          <div className="w-full sm:w-26">
            <SelectIconDropdown
              label="Icon"
              // Matches existing feature-spec selector.
              dataTestId="select_icon"
              placeholder={placeholders?.icon}
              value={siteData?.icon}
              setValue={value => handleSiteDataChange('icon', value?.value)}
              spaceY={spaceY}
              labelTextSize={labelTextSize}
              minHeight={40}
              inputHeight="fit-content"
              fontSize={selectFontSize}
              inputWidth="w-full sm:w-26"
              menuMinWidth="150px"
              isIconAsValue
            />
          </div>
          <TextInput
            label="Site Name"
            name="site_name"
            // Stable selector for name field in e2e tests.
            dataTestId="site-form-name-input"
            value={siteData?.name || ''}
            setValue={(value) => handleSiteDataChange('name', value)}
            asteriskWidth={asteriskWidth}
            isRequired
            spaceY={spaceY}
            className={inputClasses}
            inputHeight="h-10"
            placeholder={placeholders?.name}
            fontSize={fontSize}
            labelTextSize={labelTextSize}
            errorMessage={errorMessage.name}
            errorMessageClass={errorMessageClass}
            validationError={validationMessage.name}
          />
        </div>
        {/* buttons */}
        <div className="flex flex-col sm:w-5/12">
          <div className="flex-1" />
          <div className="flex space-x-4">
            <div className="flex-1 order-last sm:order-first" />
            <Button
              label="Cancel"
              size="md"
              onClick={onClickCancel}
              color="secondary"
              dataTestId="site-form-cancel-button"
            />
            <Button
              label="Save"
              size="md"
              type="submit"
              onClick={onSave}
              isDisabled={isSendingForm}
            />
          </div>
        </div>
      </div>

      {/* The rest of form, 2 columns on 640px and up displays, 1 column on smaller displays  */}
      <div className={columnsClasses}>

        {/* left column */}
        <div className="flex flex-col gap-y-12 sm:w-7/12">
          {/* site image and map */}
          <div className="flex sm:flex-row justify-between items-center h-[300px] w-full space-x-6">
            <div className="w-full h-full rounded-10px overflow-hidden">
              <PlaceholderImageWithButton onClick={handleOpenModal}>
                <SiteImage images={siteImages} siteId={siteData.id} organizationId={siteData.organization_id} heightStyle="h-[350px]" />
              </PlaceholderImageWithButton>
              <SitePhotoModal
                siteId={siteData.id}
                organizationId={siteData.organization_id}
                isShow={modals.editImages?.isOpen}
                onClose={handleCloseModal}
                updateCarouselImages={setSiteImages}
                currentSiteImages={currentSiteImages}
                setCurrentSiteImages={setCurrentSiteImages}
              />
            </div>
          </div>
          {/* Site Status, H2A and County */}
          <div className={inputGroupClasses}>
            <div className={inputInGroupClass}>
              <CustomSelect
                label="Status"
                // Dedicated test hook for status dropdown.
                dataTestId="statusDropdownMenu"
                options={statusOptions}
                value={handleDisplayStatus(siteData?.status)}
                setValue={(value) => handleSiteDataChange('status', value?.value)}
                spaceY={spaceY}
                minHeight={40}
                fontSize={selectFontSize}
                inputHeight="fit-content"
                labelFontSize={labelTextSize}
              />
            </div>
            <div className={inputInGroupClass}>
              <CustomSelect
                label="H2A"
                // Dedicated test hook for H2A dropdown.
                dataTestId="h2aDropdownMenu"
                options={h2aOptions}
                value={handleH2AValue(siteData?.h2a, siteData?.id)}
                setValue={(value) => handleSiteDataChange('h2a', value?.value)}
                spaceY={spaceY}
                minHeight={40}
                placeholder={placeholders?.h2a}
                fontSize={selectFontSize}
                inputHeight="fit-content"
                labelFontSize={labelTextSize}
                customSingleValue={customSingleValue}
              />
            </div>
            <div className={inputInGroupClass}>
              <TextInput
                label="County"
                name="county"
                value={siteData?.county || ''}
                setValue={(value) => handleSiteDataChange('county', value)}
                spaceY={spaceY}
                className={inputClasses}
                inputHeight="h-10"
                placeholder={placeholders?.county}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
              />
            </div>
          </div>
          {/* Site address, description and memo */}
          <section className={sectionsClasses.sectionClass}>
            <div className={sectionsClasses.sectionDivClass}>
              <h3 className={sectionsClasses.headerClass}>Address</h3>

              <div className={`${inputGroupClasses} mb-4`}>
                <div className={inputInGroupClass}>
                  <TextInput
                    label="Address Line 1"
                    name="address_line_1"
                    value={siteData?.address1 || ''}
                    setValue={(value) => handleSiteDataChange('address1', value)}
                    spaceY={spaceY}
                    className={inputClasses}
                    inputHeight="h-10"
                    placeholder={placeholders?.address1}
                    fontSize={fontSize}
                    labelTextSize={labelTextSize}
                  />
                </div>
                <div className={inputInGroupClass}>
                  <TextInput
                    label="Address Line 2"
                    name="address_line_2"
                    value={siteData?.address2 || ''}
                    setValue={(value) => handleSiteDataChange('address2', value)}
                    spaceY={spaceY}
                    className={inputClasses}
                    inputHeight="h-10"
                    placeholder={placeholders?.address2}
                    fontSize={fontSize}
                    labelTextSize={labelTextSize}
                  />
                </div>
                {/* Empty div for alignment */}
                <div className={inputInGroupClass} />

              </div>
              <div className={`${inputGroupClasses} mb-4`}>
                <div className={inputInGroupClass}>
                  <TextInput
                    label="City"
                    name="city"
                    value={siteData?.city || ''}
                    setValue={(value) => handleSiteDataChange('city', value)}
                    spaceY={spaceY}
                    className={inputClasses}
                    inputHeight="h-10"
                    placeholder={placeholders?.city}
                    fontSize={fontSize}
                    labelTextSize={labelTextSize}
                  />
                </div>
                <div className={inputInGroupClass}>
                  <CustomSelect
                    label="State"
                    // Dedicated test hook for state dropdown.
                    dataTestId="stateDropdownMenu"
                    isRequired
                    asteriskWidth={asteriskWidth}
                    options={stateOptions}
                    value={handleDisplayState(siteData?.state)}
                    setValue={(value) => handleSiteDataChange('state', value?.value)}
                    spaceY={spaceY}
                    minHeight={40}
                    placeholder={placeholders?.state}
                    fontSize={selectFontSize}
                    inputHeight="fit-content"
                    labelFontSize={labelTextSize}
                    errorMessage={errorMessage.state}
                    errorMessageClass={errorMessageClass}
                  />
                </div>
                <div className={inputInGroupClass}>
                  <TextInput
                    label="ZIP"
                    name="zip"
                    value={siteData?.zip_code || ''}
                    setValue={(value) => handleSiteDataChange('zip_code', value)}
                    spaceY={spaceY}
                    inputHeight="h-10"
                    placeholder={placeholders?.zip_code}
                    fontSize={fontSize}
                    labelTextSize={labelTextSize}
                    validationError={
                      isUSAZipCode(siteData?.zip_code) || siteData?.zip_code === ''
                        ? ''
                        : 'Please Enter Valid ZIP Code'
                    }
                    position="relative"
                    errorMessageClass={errorMessageClass}
                  />

                </div>
              </div>
              <div className={`${inputGroupClasses} mb-4`}>
                <TextArea
                  label="Description"
                  name="description"
                  value={siteData?.description}
                  setValue={(value) => handleSiteDataChange('description', value)}
                  spaceY="space-y-0.5"
                  placeholder={placeholders?.description}
                  className="p-2"
                  labelClassName={labelFontSize}
                  rows={3}
                  fontSize="text-sm"
                />
              </div>
              <TextInput
                label="Memo"
                name="memo"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.memo || ''}
                setValue={(value) => handleSiteDataChange('memo', value)}
                spaceY={spaceY}
                placeholder={placeholders?.memo}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
              />
            </div>
          </section>
          {/* Primary contact section */}
          <section className={sectionsClasses.sectionClass}>
            <div className={sectionsClasses.sectionDivClass}>
              <h3 className={sectionsClasses.headerClass}>Primary Contact</h3>
              <div className={inputGroupClasses}>
                <div className="w-full sm:w-2/3">
                  <CustomSelect
                    label="Contact"
                    options={contactOptions}
                    shouldShowClearOption
                    value={primaryContactName}
                    setValue={(opt) => {
                      if (!opt) {
                        setPrimaryContactName('');
                        handleSiteDataChange('primary_contact_id', '');
                        return;
                      }
                      setPrimaryContactName(opt.label);
                      handleSiteDataChange('primary_contact_id', opt.value);
                    }}
                    spaceY={spaceY}
                    minHeight={40}
                    placeholder={placeholders?.contact}
                    fontSize={selectFontSize}
                    inputHeight="fit-content"
                    labelFontSize={labelTextSize}
                    dataTestId="primary-contact"
                  />
                </div>
              </div>

              {/* TODO: Temporarily hiding the "New Contact" button — functionality will be added after launch */}
              {false && (
                <Button
                  label="New Contact"
                  size="md"
                  Icon={Plus}
                  onClick={() => { }}
                  className="max-w-min"
                />
              )}

            </div>
          </section>
          {/* Tag section */}
          <section className={sectionsClasses.sectionClass}>
            <div className="flex mt-2.5 w-full justify-center items-center">
              <SiteTags
                handleOpenDeleteModal={handleOpenDeleteTagModal}
                assignedTags={assignedTags}
                setShowTagModal={handleOpenTagModal}
                fontTagLabelSize="text-lg"
              />
              {modals.tagModal?.isOpen && (
                <TagModal
                  handleClose={handleCloseTagModal}
                  siteIds={[siteData?.id]}
                  orgId={organization.id}
                  onCreate={onCreateTag}
                  onDelete={onDeleteTag}
                  title="Tags"
                  isTagAsTabShowing
                  isCreateNewTabShowing
                  existingTags={assignedTags}
                />
              )}
            </div>
          </section>
        </div>
        {/* right column */}
        <div className="flex flex-col gap-y-12 sm:w-5/12">
          {/* Additional Site data */}
          <section className={sectionsClasses.rightUpperSectionClass}>
            <h3 className={sectionsClasses.headerClass}>Site</h3>
            <div className="mb-4">
              <TextInput
                label="Alternate IDs"
                name="alternate_ids"
                // Stable selector for alternate IDs in e2e flows.
                dataTestId="site-form-alternate-ids-input"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.alternate_ids || ''}
                setValue={(value) => handleSiteDataChange('alternate_ids', value)}
                spaceY={spaceY}
                placeholder={placeholders?.alternateID}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
              />
            </div>
            <div className="mb-4">
              <TextInput
                label="Alternate Names"
                name="alternate_names"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.alternate_names || ''}
                setValue={(value) => handleSiteDataChange('alternate_names', value)}
                spaceY={spaceY}
                placeholder={placeholders?.alternateName}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
              />
            </div>
            <div className="mb-4">
              <CustomSelect
                label="Site Type"
                options={siteTypeOptions}
                value={siteData?.site_type}
                shouldShowClearOption
                setValue={(opt) => handleSiteDataChange('site_type', opt?.label ?? '')}
                spaceY="space-y-0.5"
                minHeight={40}
                placeholder={placeholders?.siteType}
                inputHeight="fit-content"
                labelBesideLabelClasses="w-1/3"
                inputWidth="w-full"
                fontSize={14}
                labelClassName="font-medium"
              />
            </div>
            <TextInput
              label="Units"
              name="units"
              className={inputClasses}
              inputHeight="h-10"
              value={siteData?.units || ''}
              setValue={(value) => handleSiteDataChange('units', value)}
              spaceY={spaceY}
              placeholder={placeholders?.units}
              fontSize={fontSize}
              labelTextSize={labelTextSize}
              labelBesideLabelClasses={labelBesideLabelClasses}
              labelBesideInputClasses={labelBesideInputClasses}
            />
          </section>
          {/* workers */}
          <section className={sectionsClasses.rightUpperSectionClass}>
            <h3 className={sectionsClasses.headerClass}>Workers</h3>
            <div className="mb-4">
              <DateSelectorV2
                label="Arrival"
                name="arrival"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.arrival && formatNumericDate(siteData?.arrival)}
                setValue={(value) => handleSiteDataChange('arrival', formatTimestampDate(value))}
                spaceY={spaceY}
                placeholder={placeholders?.arrival}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
                inputStyle={{ paddingLeft: 12 }}
                dateFormat="MM/dd/yyyy"
                iconRight={4}
                iconBottom={4}
              />
            </div>
            <div className="mb-4">
              <DateSelectorV2
                label="Departure"
                name="departure"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.departure && formatNumericDate(siteData?.departure)}
                setValue={(value) => handleSiteDataChange('departure', formatTimestampDate(value))}
                spaceY={spaceY}
                placeholder={placeholders?.departure}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
                inputStyle={{ paddingLeft: 12 }}
                dateFormat="MM/dd/yyyy"
                iconRight={4}
                iconBottom={4}
              />
            </div>

            <Number
              label="Worker Count"
              name="worker_count"
              className={inputClasses}
              inputHeight="h-10"
              value={siteData?.worker_count}
              setValue={(value) => handleSiteDataChange('worker_count', value)}
              spaceY="space-y-0.5"
              placeholder={placeholders?.workerCount}
              labelBesideLabelClasses="w-1/3"
              labelBesideInputClasses="w-2/3"
              fontSize="text-sm"
              labelFontSize="text-base font-normal"
              min={0}
              errorMessage={errorMessage.worker_count}
            />
          </section>
          {/* location */}
          <section className={sectionsClasses.rightUpperSectionClass}>
            <h3 className={sectionsClasses.headerClass}>Location</h3>
            <div className="mb-4">
              <TextInput
                label="Region"
                name="region"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.region || ''}
                setValue={(value) => handleSiteDataChange('region', value)}
                spaceY={spaceY}
                placeholder={placeholders?.region}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
              />
            </div>
            <div className="flex flex-col sm:flex-row">
              <HeaderIconTooltip
                heading="Coordinates"
                divTextStart="Vamos adds coordinates automatically"
                divTextEnd={(
                  <>
                    <div>from the address. To set coordinates</div>
                    <div>manually, toggle this button and enter</div>
                    <div>values or drag the map marker.</div>
                  </>
                )}
                place="top"
                iconName="icon_circle"
                id="relatedSites"
                tooltipId="coordinates-tooltip"
                margins="" // remove margins
                labelFontClassName="text-base font-normal"
                border="" // remove border
                gap="gap-2"
                paddingBottom=""
              />
              <div className="flex-1" />
              <ToggleButton
                isOn={siteData.coords_set_manually}
                onClick={toggleCoordinatesButton}
                label="set manually"
                id="toggle-set-manually"
              />
            </div>
            <div className="mb-4">
              <TextInput
                label="Latitude"
                name="latitude"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.latitude || ''}
                setValue={(value) => handleSiteDataChange('latitude', value)}
                spaceY={spaceY}
                placeholder={placeholders?.latitude}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
                isDisabled={!siteData?.coords_set_manually}
              />
            </div>
            <div className="mb-4">
              <TextInput
                label="Longitude"
                name="longitude"
                className={inputClasses}
                inputHeight="h-10"
                value={siteData?.longitude || ''}
                setValue={(value) => handleSiteDataChange('longitude', value)}
                spaceY={spaceY}
                placeholder={placeholders?.longitude}
                fontSize={fontSize}
                labelTextSize={labelTextSize}
                labelBesideLabelClasses={labelBesideLabelClasses}
                labelBesideInputClasses={labelBesideInputClasses}
                isDisabled={!siteData?.coords_set_manually}
              />
            </div>
            <TextArea
              label="Directions"
              name="directions"
              value={siteData?.directions}
              setValue={(value) => handleSiteDataChange('directions', value)}
              spaceY="space-y-0.5"
              placeholder={placeholders?.directions}
              labelTextSize={labelTextSize}
              labelClassName="w-1/3 pt-2"
              inputWidth="w-2/3"
              labelFontSize="text-base font-normal"
              className="p-2"
              labelBesideClasses="flex"
              fontSize="text-sm"
            />

          </section>
          {/* Custom fields section */}
          <section className={sectionsClasses.rightUpperSectionClass}>
            <HeaderIconTooltip
              heading="Custom Fields"
              spanTextStart="Vamos Managers can add additional fields in the"
              linkUrl="/admin/custom_fields"
              spanTextEnd="page to capture data specific to your organization."
              linkText="Customize Forms"
              place="top"
              iconName="icon_circle"
              id="relatedSites"
              tooltipId="custom_field-tooltip"
              margins="" // remove margins
              labelFontClassName={sectionsClasses.headerClass}
              border="" // remove border
              gap="gap-2"
              paddingBottom="0"
            />
            {siteData.custom_fields && (
              <CustomFieldsForm
                customFields={siteData.custom_fields}
                setData={setSiteData}
                isHideHeader
                inputFieldsHeight="h-10"
                inputHeightSelect={40}
                iconRight={4}
                iconBottom={4}
                errorMessage={errorMessage}
                errorMessageClass={errorMessageClass}
                setCustomFieldsChanged={setCustomFieldsChanged}
                shouldScrollToError={shouldScrollToError}
              />
            )}
          </section>
        </div>
      </div>
    </>
  );
};

SiteFormWrapper.propTypes = {
  onSave: PropTypes.func.isRequired,
  organization: PropTypes.object.isRequired,
  siteData: PropTypes.object.isRequired,
  setSiteData: PropTypes.func.isRequired,
  orgContacts: PropTypes.array.isRequired,
  assignedTags: PropTypes.array.isRequired,
  setAssignedTags: PropTypes.func.isRequired,
  errorMessage: PropTypes.object.isRequired,
  primaryContactName: PropTypes.string.isRequired,
  setPrimaryContactName: PropTypes.func.isRequired,
  organizationTags: PropTypes.array.isRequired,
  currentSiteImages: PropTypes.array.isRequired,
  setCurrentSiteImages: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  isSendingForm: PropTypes.bool.isRequired,
  isUnsavedChanges: PropTypes.bool.isRequired,
  setCustomFieldsChanged: PropTypes.func.isRequired,
  validationMessage: PropTypes.object.isRequired,
  setIsUnsavedChanges: PropTypes.func.isRequired,
  // eslint-disable-next-line react/boolean-prop-naming
  hasUnsavedChanges: PropTypes.bool.isRequired,
  shouldScrollToError: PropTypes.bool.isRequired,
};

export default SiteFormWrapper;
